"use client";
import { FlexColumn, FlexColumnJustifyCenter, FlexRowAlignCenter } from "@/components/Flex";
import LazyLoaded from "@/components/LazyLoaded";
import EditableFlashcardCard from "@/components/flashcards/flashcardsCard/EditableFlashcardCard";
import InputWithSearchbarIcon from "@/components/styled/input/InputWithSearchbarIcon";
import { useGlobalState } from "@/experimental/useGlobalState";
import useDOMEventListener from "@/hooks/useDOMEventListener";
import br from "@/styles/breakpoints.module.css";
import { themeColors } from "@/utils/themeColors";
import { Flashcard, FlashcardSet, Language, SubscriptionType } from "@knowt/syncing/graphql/schema";
import { MIN_FLASHCARDS_COUNT } from "@knowt/syncing/hooks/flashcards/utils";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { arraySwap } from "@knowt/syncing/utils/arrayUtils";
import Zoom from "@mui/material/Zoom";
import clsx from "clsx";
import { ArrowLeftRight, Settings } from "lucide-react";
import dynamic from "next/dynamic";
import { useCallback, useMemo, useRef, useState } from "react";
import { isMacOs } from "react-device-detect";
import { Virtuoso } from "react-virtuoso";
import { useSetEditingContextSelector } from "../hooks/useSetEditingContext";
import { FlashcardForceUpdateContentCb, FlashcardSortable } from "../types";
import { flashcardsToSortable } from "../utils";
import AddFlashcardBtn from "./AddFlashcardBtn";
import ScrollToTopFab from "./ScrollToTopFab";
import { SetEditingDndContext, SetEditingSortableContext, SetEditingSortableNode } from "./SetEditingDndHandlers";
import CircularOutlineIcon from "@/components/CircularButton/styled/CircularOutlineIcon";
import { iconSizes } from "@/utils/iconProps";

const CopilotSettingsPopup = dynamic(() => import("@/components/Popup/CopilotSettingsPopup"), { ssr: false });

const FlashcardListTopBar = ({
    query,
    onQueryChange,
    flashcardSet,
    onSwapTermDefinition,
}: {
    query: string;
    onQueryChange: (s: string) => void;
    flashcardSet: FlashcardSet;
    onSwapTermDefinition: () => void;
}) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const setIsCopilotSettingsOpen = useSetEditingContextSelector(s => s.setIsCopilotSettingsOpen);
    const isCopilotSettingsOpen = useSetEditingContextSelector(s => s.isCopilotSettingsOpen);

    useDOMEventListener({
        on: "keydown",
        handler: async (event: KeyboardEvent) => {
            const isCtrlKey = isMacOs ? event.metaKey : event.ctrlKey;
            if (!(event.key === "F3" || (isCtrlKey && event.key === "f"))) return;
            event.preventDefault();

            inputRef.current?.focus();
        },
    });

    return (
        <FlexRowAlignCenter
            className={br.smDownWrap}
            style={{ justifyContent: "space-between", gap: "1rem", marginBottom: "1rem" }}>
            <InputWithSearchbarIcon
                ref={inputRef}
                onChange={e => onQueryChange(e.target.value)}
                value={query}
                placeholder={"Search terms/definitions"}
                containerStyle={{ width: "100%" }}
                className={clsx("body2", br.smDownWidth100, "ellipsisText")}
                sx={{
                    width: "30rem",
                    transition: "width 0.3s",
                    color: themeColors.neutralBlack,
                    backgroundColor: themeColors.neutralWhite,
                    "&:focus": { width: "50rem" },
                }}
            />
            <LazyLoaded load={isCopilotSettingsOpen}>
                <CopilotSettingsPopup
                    isOpen={isCopilotSettingsOpen}
                    onClose={() => setIsCopilotSettingsOpen(false)}
                    flashcardSetId={flashcardSet.flashcardSetId}
                />
            </LazyLoaded>
            <CircularOutlineIcon
                radius="4rem"
                Icon={Settings}
                tooltip="Kai Complete"
                onClick={() => setIsCopilotSettingsOpen(true)}
                sx={{ marginLeft: "auto", backgroundColor: themeColors.neutralWhite, border: "none" }}
                size={iconSizes.SM}
            />
            <CircularOutlineIcon
                radius="4rem"
                onClick={onSwapTermDefinition}
                Icon={ArrowLeftRight}
                size={iconSizes.SM}
                sx={{ backgroundColor: themeColors.neutralWhite, border: "none" }}
                tooltip="Swap terms and definitions"
            />
        </FlexRowAlignCenter>
    );
};

const StickyFab = ({ onClick }: { onClick: () => void }) => {
    return (
        <div tabIndex={-1} style={{ position: "sticky", height: 0, bottom: "9rem", left: "3rem", zIndex: 1000 }}>
            <ScrollToTopFab onScroll={onClick} />
        </div>
    );
};

const PadHoverableAddFlashcardBtn = ({ onClick }) => {
    const [isHover, setIsHover] = useState(false);
    return (
        <FlexColumnJustifyCenter
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
            style={{ flex: 1, height: "1rem" }}>
            <Zoom in={isHover} timeout={200}>
                <div style={{ margin: "auto" }}>
                    <AddFlashcardBtn onClick={onClick} />
                </div>
            </Zoom>
        </FlexColumnJustifyCenter>
    );
};

/** `SetEditingFlashcardList` does not rerender `Virtuoso` when it's inner content force to change. This hook can trigger Virtuoso to rerender all item in the list */
export const useRerenderFlashcardList = () => {
    const [rerenderKey, setKey] = useGlobalState(`setEditingFlashcardListRerender`, 0);

    /** Rerender all flashcards in the `SetEditingFlashcardList` -> `Virtuoso` list */
    const rerenderFlashcardList = () => {
        setKey(Math.random());
    };

    return { rerenderKey, rerenderFlashcardList };
};

const SetEditingFlashcardList = () => {
    const { subscriptionType } = useCurrentUser();
    const [query, setQuery] = useState("");

    const virtuosoRef = useSetEditingContextSelector(s => s.virtuosoRef);
    const handleSwapTermsDefinitions = useSetEditingContextSelector(s => s.handleSwapTermsDefinitions);
    const pendingUpdates = useSetEditingContextSelector(s => s.pendingUpdates);
    const updateFlashcardSetMetaData = useSetEditingContextSelector(s => s.updateFlashcardSetMetaData);
    const deleteFlashcard = useSetEditingContextSelector(s => s.deleteFlashcard);
    const updateFlashcards = useSetEditingContextSelector(s => s.updateFlashcards);
    const flashcardsHandlesMap = useSetEditingContextSelector(s => s.flashcardsHandlesMap);
    const addFlashcard = useSetEditingContextSelector(s => s.addFlashcard);
    const flashcardSet = useSetEditingContextSelector(s => s.flashcardSet);
    const flashcards = flashcardSet?.flashcards;

    const { rerenderKey } = useRerenderFlashcardList();

    const disableDelete = (flashcards?.length ?? 0) <= MIN_FLASHCARDS_COUNT;

    const filteredFlashcards: FlashcardSortable[] = useMemo(() => {
        let result = (flashcards || []) as Flashcard[];

        if (!query) {
            result = flashcards as Flashcard[];
        }

        result = flashcards?.filter(f => {
            return (
                f?.term?.toLowerCase().includes(query.toLowerCase()) ||
                f?.definition?.toLowerCase().includes(query.toLowerCase())
            );
        }) as Flashcard[];

        return flashcardsToSortable({ flashcards: result });
    }, [flashcards, query]);

    const handleDragEnd = useCallback(
        ({ active, over }: { active: FlashcardSortable; over: FlashcardSortable }) => {
            if (active.flashcardId === over.flashcardId) {
                return;
            }

            const newItems = arraySwap(flashcards as Flashcard[], active.index, over.index);
            updateFlashcards(newItems);
        },
        [flashcards, updateFlashcards]
    );

    const addFlashcardAtIndex = useCallback(
        async (newIndex: number) => {
            const flashcard = await addFlashcard({ insertAt: newIndex });
            if (!flashcard) return;

            setTimeout(() => {
                virtuosoRef?.current?.scrollToIndex({ index: newIndex - 1, behavior: "smooth" });
            }, 25);
        },
        [addFlashcard, virtuosoRef]
    );

    if (!filteredFlashcards || !flashcardSet) {
        return null;
    }

    const lenCardDisplay = filteredFlashcards.length;
    const isCustomizationBlocked = subscriptionType === SubscriptionType.BASIC;

    return (
        <FlexColumn style={{ position: "relative" }}>
            <FlashcardListTopBar
                query={query}
                onQueryChange={q => setQuery(q)}
                onSwapTermDefinition={handleSwapTermsDefinitions}
                flashcardSet={flashcardSet}
            />
            <SetEditingDndContext onDragEnd={handleDragEnd}>
                <SetEditingSortableContext items={filteredFlashcards}>
                    <Virtuoso
                        key={rerenderKey}
                        ref={virtuosoRef}
                        useWindowScroll
                        increaseViewportBy={200}
                        data={filteredFlashcards}
                        itemContent={(index, f) => {
                            const isLast = index === lenCardDisplay - 1;

                            return (
                                <SetEditingSortableNode key={f.id} id={f.id} item={f}>
                                    <EditableFlashcardCard
                                        ref={(handle: FlashcardForceUpdateContentCb) => {
                                            const flashcardId = f?.flashcardId;
                                            if (flashcardsHandlesMap) {
                                                if (handle) flashcardsHandlesMap.set(flashcardId, handle);
                                                else flashcardsHandlesMap.delete(flashcardId);
                                            }
                                        }}
                                        flashcard={f}
                                        index={f.index}
                                        termLanguage={flashcardSet?.termLanguage as Language}
                                        definitionLanguage={flashcardSet?.definitionLanguage as Language}
                                        disableDelete={disableDelete}
                                        deleteFlashcard={deleteFlashcard}
                                        updateFlashcardSetMetaData={updateFlashcardSetMetaData}
                                        pendingUpdates={pendingUpdates}
                                        isCustomizationBlocked={isCustomizationBlocked}
                                    />
                                    {!isLast && (
                                        <PadHoverableAddFlashcardBtn onClick={() => addFlashcardAtIndex(index + 1)} />
                                    )}
                                </SetEditingSortableNode>
                            );
                        }}
                    />
                </SetEditingSortableContext>
            </SetEditingDndContext>

            <StickyFab
                onClick={() => {
                    virtuosoRef?.current?.scrollToIndex({
                        index: lenCardDisplay - 1,
                        offset: -150,
                        behavior: "smooth",
                    });
                }}
            />
        </FlexColumn>
    );
};

export default SetEditingFlashcardList;
